import type { Enums, Nullable } from "@msuite/utils/types";
import { motion } from "motion/react";
import React from "react";
import { Icon, type IconType } from "../icons";
import { StatusIndicator } from "../status-indicator";
import { cn } from "../utils";
import { useMenuContext } from "./context";

export interface MenuLabelType {
	label: string;
}

export interface MenuItemType {
	icon: Nullable<IconType>;
	iconColor?: Enums<"color">;
	label: string;
	items?: Array<MenuItemType | "divider">;
	onClick?: (() => void | Promise<void>) | undefined;
	isDanger?: boolean;
	isActive?: boolean;
	isDisabled?: boolean;
	/**
	 * Close the menu once this item is chosen. Off by default: a menu stays open after a click,
	 * which suits a list of toggles the user works through in one go — so an item that is an
	 * action done once, collapsing a card or removing a row, says so here.
	 */
	closesMenu?: boolean;
	render?: () => React.ReactNode;
	renderIsItem?: boolean;
	cyId?: string;
}

/** Props Interface */
interface MenuItemProps extends MenuItemType {
	maxHeight?: number;
}

export function MenuItem(props: MenuItemProps) {
	/** Computed */
	const { TypeComponent, SubMenu } = useMenuContext();

	/** Props */
	const MenuIcon = props.icon;
	const hasIcon = MenuIcon !== undefined;
	const hasItems = (props.items ?? []).length > 0;

	/** Computed */
	const hasClickFunction = typeof props.onClick === "function";
	const Wrapper = hasItems ? TypeComponent.Sub : React.Fragment;
	const Item =
		props.render && !props.renderIsItem
			? motion.div
			: hasItems
				? TypeComponent.SubTrigger
				: TypeComponent.Item;

	/** Render */
	return (
		<Wrapper>
			<Item
				key={props.label}
				data-cy={props.cyId}
				disabled={props.isDisabled}
				className={cn(
					"data-disabled:pointer-events-none data-disabled:opacity-50 data-disabled:cursor-not-allowed! relative",
					props.render && !props.renderIsItem
						? "listbox-option-shape listbox-option-custom-render"
						: props.isDanger
							? "listbox-option-danger"
							: "listbox-option",
					!hasClickFunction && "cursor-default!",
					"h-stack",
				)}
				onSelect={(e) => {
					e.stopPropagation();
					// Radix keeps the menu open when the select event is cancelled, and that is the
					// default here; an item marked `closesMenu` lets the menu close on its own.
					if (!props.closesMenu) e.preventDefault();
					props.onClick?.();
				}}
				textValue={props.label}
			>
				{!props.render ? (
					<div
						className="grid w-full items-center justify-center"
						style={{ gridTemplateColumns: "20px 1fr 20px" }}
					>
						{hasIcon && MenuIcon && <MenuIcon />}
						<span className="col-start-2">{props.label}</span>
						{hasItems && (
							<div className="ml-auto">
								<Icon name="ChevronRight" />
							</div>
						)}
					</div>
				) : (
					props.render()
				)}
				{props.isActive && (
					<div className="absolute top-[50%] right-0 translate-y-[-50%] -translate-x-4">
						<StatusIndicator color="primary" />
					</div>
				)}
			</Item>
			{hasItems && SubMenu && (
				<SubMenu
					items={props.items}
					type="sub"
					maxHeight={props.maxHeight}
				/>
			)}
		</Wrapper>
	);
}
